import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faTimes,
  faTrash,
  faUserCheck,
  faInfoCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "./button";

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message = "This action cannot be undone.",
  confirmText = "Delete",
  cancelText = "Cancel",
  type = "danger",
  icon,
  isLoading = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape" && !isLoading) onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const typeStyles = {
    danger: {
      headerFrom: "from-red-50",
      iconBg: "bg-red-100",
      iconColor: "text-red-700",
      borderColor: "border-red-100",
      titleColor: "text-gray-800",
      buttonBg: "bg-red-700 hover:bg-red-800",
      buttonIcon: faTrash,
    },
    warning: {
      headerFrom: "from-amber-50",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-700",
      borderColor: "border-amber-100",
      titleColor: "text-gray-800",
      buttonBg: "bg-amber-700 hover:bg-amber-800",
      buttonIcon: faExclamationTriangle,
    },
    info: {
      headerFrom: "from-blue-50",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-800",
      borderColor: "border-blue-100",
      titleColor: "text-gray-800",
      buttonBg: "bg-blue-800 hover:bg-blue-900",
      buttonIcon: faInfoCircle,
    },
    success: {
      headerFrom: "from-green-50",
      iconBg: "bg-green-100",
      iconColor: "text-green-700",
      borderColor: "border-green-100",
      titleColor: "text-gray-800",
      buttonBg: "bg-green-700 hover:bg-green-800",
      buttonIcon: faUserCheck,
    },
  };

  const style = typeStyles[type] || typeStyles.danger;
  const finalIcon = icon || style.buttonIcon;

  const handleConfirmClick = () => {
    if (isLoading) return;
    onConfirm();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pb-6">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={isLoading ? undefined : onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 z-10 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
        <button
          onClick={onClose}
          disabled={isLoading}
          aria-label="Close dialog"
          className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faTimes} className="h-3.5 w-3.5" />
        </button>

        <div className="p-4 pb-6">
          <div
            className={`flex items-center gap-3 p-3 bg-gradient-to-r ${style.headerFrom} to-white rounded-lg border ${style.borderColor}`}
          >
            <div
              className={`w-10 h-10 rounded-full ${style.iconBg} flex items-center justify-center shrink-0`}
            >
              <FontAwesomeIcon
                icon={finalIcon}
                className={`${style.iconColor} h-4 w-4`}
              />
            </div>
            <div>
              <p className={`text-xs ${style.titleColor} font-semibold`}>
                {title}
              </p>
              <p className="text-xs text-gray-600">
                Please confirm to continue.
              </p>
            </div>
          </div>

          <p className="mt-4 text-sm text-gray-700 leading-relaxed px-1">
            {message}
          </p>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 px-4 pb-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={isLoading}
            className={`gap-2 w-full sm:w-auto ${style.buttonBg} text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={finalIcon} className="h-3.5 w-3.5" />
                {confirmText}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
